import {
  INV_AVAILABILITY_IN_REQUEST_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../../constants/inventory-status-labels';
import { InventoryItem } from '../../types/inventory';

const IN_TRANSIT_LABELS = new Set(['En Transito', 'En tránsito']);

function inferPlacementStatus(item: InventoryItem): string | undefined {
  if (item.originalStatus != null && item.originalStatus !== '') {
    return item.originalStatus;
  }
  if (item.status === INV_STATUS_BRANCH_LABEL) {
    return 'ASSIGNED';
  }
  if (item.status === INV_STATUS_WAREHOUSE_LABEL) {
    return 'UNASSIGNED';
  }
  if (IN_TRANSIT_LABELS.has(item.status)) {
    return 'IN_TRANSIT';
  }
  return undefined;
}

function inferLocationType(item: InventoryItem): 'WAREHOUSE' | 'BRANCH' | undefined {
  if (item.locationType != null) {
    return item.locationType;
  }
  const placementStatus = inferPlacementStatus(item);
  if (placementStatus === 'UNASSIGNED') {
    return 'WAREHOUSE';
  }
  if (placementStatus === 'ASSIGNED') {
    return 'BRANCH';
  }
  return undefined;
}

/** Mirrors backend `isWarehouseInventoryProductEligibleForRequest`. */
export function isSelectableForInventoryRequest(item: InventoryItem): boolean {
  if (item.availabilityStatus !== 'Disponible') {
    return false;
  }

  const placementStatus = inferPlacementStatus(item);
  if (placementStatus === 'IN_TRANSIT' || IN_TRANSIT_LABELS.has(item.status)) {
    return false;
  }

  const locationType = inferLocationType(item);
  if (locationType === 'WAREHOUSE') {
    return placementStatus === 'UNASSIGNED' && item.transferredToBranch !== true;
  }
  if (locationType === 'BRANCH') {
    return placementStatus === 'ASSIGNED' && item.transferredToBranch === true;
  }

  return false;
}

export function resolveLockedOriginLocationName(
  selectedItems: string[],
  itemById: Map<string, InventoryItem>,
): string | null {
  for (const id of selectedItems) {
    const item = itemById.get(id);
    if (item?.locationName) {
      return item.locationName;
    }
  }
  return null;
}

export function getInventoryRequestSelectionState(
  item: InventoryItem,
  lockedOriginLocationName: string | null,
): { selectable: boolean; reason: string } {
  if (item.availabilityStatus === INV_AVAILABILITY_IN_REQUEST_LABEL) {
    return {
      selectable: false,
      reason: 'La pieza está en solicitud',
    };
  }
  if (!isSelectableForInventoryRequest(item)) {
    return {
      selectable: false,
      reason: `No se puede seleccionar: estado "${item.status}" o disponibilidad "${item.availabilityStatus}"`,
    };
  }
  if (
    lockedOriginLocationName != null &&
    lockedOriginLocationName !== '' &&
    item.locationName != null &&
    item.locationName !== '' &&
    item.locationName !== lockedOriginLocationName
  ) {
    return {
      selectable: false,
      reason: `Solo piezas del mismo origen (${lockedOriginLocationName})`,
    };
  }
  return {
    selectable: true,
    reason: 'Haz clic para seleccionar esta pieza',
  };
}
